<script lang="ts" setup>
import type { ToastData } from '@/objects/ToastData';
import { Toast } from 'bootstrap';
import { onMounted, ref, computed, type ComponentInstance } from 'vue';
import { ModalComp, DateComp, IconComp, ButtonComp, ErrorReportForm, ContentComp, type ButtonTypes } from '@/components';
import { $ti, $tm, featureIcons } from '@/.';
import { MailRequest } from '@/objects/generated';
import { CommunicationDao } from '@/dao';
import { useEventStoreRef, useUserStore } from '@/stores';

const { user } = useUserStore();

const props = defineProps<{ toast: ToastData }>();
const emit = defineEmits<{
    (e: 'remove', toast: ToastData): void;
}>();

const { eventID } = useEventStoreRef();
const self = ref<HTMLElement | null>(null);
const modaldialog = ref<ComponentInstance<typeof ModalComp>>();
const reportErrorModal = ref<ComponentInstance<typeof ModalComp>>();
const errorReport = ref<MailRequest>(MailRequest.FromJsValues({ Sender: user?.Email, SenderName: user?.Name }));

const showModalDialog = () => modaldialog.value?.show();

// Get the background color for the toast
const bgColor = computed(() =>
    props.toast.level == 'INFO'
        ? 'bg-info'
        : props.toast.level == 'WARNING'
          ? 'bg-warning'
          : props.toast.level == 'ERROR'
            ? 'bg-danger'
            : 'bg-success',
);

const btnType = computed<ButtonTypes>(() => {
    return props.toast.level == 'INFO'
        ? 'Info'
        : props.toast.level == 'WARNING'
          ? 'Warning'
          : props.toast.level == 'ERROR'
            ? 'Danger'
            : 'Success';
});

// Get the text color for the toast
const textColor = computed(() => (props.toast.level == 'WARNING' ? 'text-black' : 'text-white'));

// Get the icon for the toast
const icon = computed(() => {
    if (props.toast.level == 'INFO') {
        return featureIcons.INFO;
    } else if (props.toast.level == 'WARNING') {
        return featureIcons.WARNING;
    } else if (props.toast.level == 'ERROR') {
        return featureIcons.ERROR;
    }

    return featureIcons.SUCCESS;
});

const textTooLong = computed(() => {
    return $tm(props.toast.message).length > 56;
});

/**
 * Submit the error report
 */
const submitErrorReport = async () => {
    errorReport.value.Message = errorReport.value.Message + '\r\n\r\nException: ' + props.toast.message;
    await CommunicationDao.ReportError(errorReport.value, eventID.value);

    // Reset the error report
    errorReport.value = MailRequest.FromJsValues({ Sender: user?.Email, SenderName: user?.Name });
    reportErrorModal.value?.hide();
};

onMounted(() => {
    if (self.value) {
        // We create a toast with the message and show it. If it is an error we do not autohide it, but if it is a success, warning or info we autohide it after 5 seconds
        const htmlToast = Toast.getOrCreateInstance(self.value, { autohide: props.toast.level != 'ERROR', delay: 5000 });
        htmlToast.show();
    }
});
</script>
<template>
    <div
        ref="self"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        class="toast"
        :class="[bgColor + '-subtle', textColor]"
        :id="'toast' + toast.id"
        v-on="{
            'hidden.bs.toast': () => emit('remove', toast),
        }"
    >
        <div class="toast-header">
            <strong class="me-auto">
                <IconComp :icon="icon" />
                {{ $tm(toast.title) }}
            </strong>
            <small
                ><DateComp
                    class="text-capitalize"
                    :value="new Date()"
                    format="DAYTIME"
                ></DateComp
            ></small>
            <ButtonComp
                :button-type="btnType"
                class="btn-xs ms-4"
                icon="fa-xmark"
                data-bs-dismiss="toast"
                aria-label="Close"
            >
            </ButtonComp>
        </div>
        <div
            class="toast-body text-truncate"
            :class="[bgColor, textColor]"
            v-html="$ti(toast.message, toast.params, true)"
        ></div>
        <div
            :class="[bgColor, textColor]"
            class="toast-footer border border-white d-flex flex-row"
            v-if="textTooLong && props.toast.level == 'ERROR'"
        >
            <ButtonComp
                v-if="textTooLong"
                :button-type="'Link'"
                textposition="left"
                class="text-white"
                @click="showModalDialog"
                >{{ $tm('common.viewall') }}</ButtonComp
            >
            <div class="flex-fill"></div>
            <ButtonComp
                v-if="props.toast.level == 'ERROR'"
                button-type="Custom"
                class="float-end text-white"
                @click="reportErrorModal?.show()"
            >
                {{ $tm('common.reporterror') }} <IconComp icon="fa-ticket-perforated" />
            </ButtonComp>
        </div>
    </div>
    <!-- Modal for showing the complete error message. -->
    <ModalComp
        v-if="textTooLong"
        :title="$tm(toast.title)"
        :title-icon="featureIcons.ERROR"
        type="danger"
        size="xl"
        ref="modaldialog"
    >
        <template #content>
            <div class="pt-5">
                <pre class="pb-5 mb-1">{{ $ti(toast.message, toast.params, true) }}</pre>
            </div>
        </template>
    </ModalComp>

    <!-- Modal for sending the error message to dev -->
    <ModalComp
        v-if="toast.level == 'ERROR'"
        :title="$tm('common.reporterror')"
        size="lg"
        ref="reportErrorModal"
    >
        <template #content>
            <ContentComp>
                <ErrorReportForm :model-value="errorReport"></ErrorReportForm>
            </ContentComp>
        </template>
        <template #footer>
            <ButtonComp
                button-type="Primary"
                @click="submitErrorReport"
                ><IconComp
                    icon="fa-paper-plane-top"
                    class="me-2"
                />{{ $tm('common.send') }}</ButtonComp
            >
        </template>
    </ModalComp>
</template>
